import { CommonModule, DOCUMENT } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  afterNextRender,
  inject,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PaginationComponent } from '../../../inputs/pagination/pagination.component';
import { ITestContact } from '../../../../types/settingsDataModule';
import { UpdateTestUserFormComponent } from '../../../forms/settingsforms/update-test-user-form/update-test-user-form.component';
import { SendTestSmsFormComponent } from '../../../forms/settingsforms/send-test-sms-form/send-test-sms-form.component';
import { SettingsService } from '../../../../services/settings/settings.service';
import { ToastService } from '../../../../services/toast/toast.service';
import { PromptCardComponent } from '../../../toasts-and-cards/prompt-card/prompt-card.component';
import { DataTableComponent } from '../../../ui/data-table/data-table.component';
import { EmptyStateComponent } from '../../../ui/empty-state/empty-state.component';
import { HasPermissionDirective } from '../../../../directives/has-permission.directive';
import { displayPhone } from '../../../../pages/organization/settings/settings-roles';
import { formatWhen } from '../../../../../utils/sms';

type FeedbackType = 'success' | 'error' | 'info' | 'warning';

/** One test number as the table shows it. */
interface TestContactRow {
  contact: ITestContact;
  key: string;
  name: string;
  email: string;
  phone: string;
  added: string;
}

@Component({
  selector: 'app-test-contacts-table',
  standalone: true,
  imports: [
    CommonModule,
    MatTooltipModule,
    PaginationComponent,
    UpdateTestUserFormComponent,
    SendTestSmsFormComponent,
    PromptCardComponent,
    DataTableComponent,
    EmptyStateComponent,
    HasPermissionDirective,
  ],
  templateUrl: './test-contacts-table.component.html',
  styleUrls: [
    '../../../../pages/organization/settings/settings-ui.css',
    './test-contacts-table.component.css',
  ],
})
export class TestContactsTableComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) testContacts: ITestContact[] = [];
  @Input() loading: boolean = false;
  @Input() params!: Record<string, any>;
  /** Zero-based page the parent loaded. */
  @Input() page = 0;
  @Input() pageSize = 10;
  @Input() totalPages = 1;
  @Input() totalItems = 0;
  /** The current search, for the empty state. */
  @Input() searchTerm = '';
  @Input() loadFailed = false;
  /** Whether this person may add, edit and delete test numbers. */
  @Input() canManage = false;
  /** Asks the parent to reload the list (after an edit or delete). */
  @Output() fetchTestContacts = new EventEmitter();
  @Output() pageChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();
  @Output() addTestContact = new EventEmitter<void>();
  @Output() retry = new EventEmitter<void>();

  settingService = inject(SettingsService);
  private toast = inject(ToastService);
  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  private injector = inject(Injector);
  private document = inject(DOCUMENT);

  /** Row position of a removed number; focus goes there once the list comes back. */
  private refocusIndex: number | null = null;

  rows: TestContactRow[] = [];
  testContactsParam = {};
  testContactsPage: number = 0;
  totalTestContactsPages: number = 1;

  showEditModal: boolean = false;
  showDeletePrompt: boolean = false;
  showSendModal = false;
  selectedTestContact: ITestContact | null = null;
  isDeleting = false;

  // Feedback goes to the toast rail
  feedbackMessage: string = '';
  feedbackType: FeedbackType = 'success';
  showFeedback: boolean = false;

  get selectedName(): string {
    const c = this.selectedTestContact;
    return c ? `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'this test number' : '';
  }

  get selectedPhone(): string {
    return displayPhone(this.selectedTestContact?.primaryContactNumber);
  }

  get emptyMessage(): string {
    if (this.loadFailed) return 'The test numbers did not load';
    if (this.searchTerm.trim()) return `No test number matches “${this.searchTerm.trim()}”`;
    return 'No test numbers yet';
  }

  get emptyHint(): string {
    if (this.loadFailed) return 'Check your connection, then try again.';
    if (this.searchTerm.trim()) return 'Search looks at first names, or at the number when you type digits.';
    return this.canManage
      ? 'Add the phones your team checks messages on. You can then send each one a test before a real send.'
      : 'Someone who manages contacts can add the phones your team checks messages on.';
  }

  ngOnInit() {
    this.buildRows();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.testContactsParam = this.params;
    this.testContactsPage = this.page;
    this.totalTestContactsPages = this.totalPages;
    this.buildRows();

    if (this.refocusIndex !== null && changes['testContacts'] && !this.loading) {
      const index = this.refocusIndex;
      this.refocusIndex = null;
      afterNextRender(() => this.refocus(index), { injector: this.injector });
    }
  }

  /**
   * The removed row took its buttons with it, so focus fell to the page.
   * Put it on the next row's first action (or the empty state's), unless the
   * person has already moved on.
   */
  private refocus(index: number) {
    const active = this.document.activeElement;
    if (active && active !== this.document.body) return;
    const root = this.host.nativeElement;
    const rows = root.querySelectorAll<HTMLElement>('tbody tr');
    const row = rows.length ? rows[Math.min(index, rows.length - 1)] : null;
    const target =
      row?.querySelector<HTMLElement>('.bk-row-actions button') ??
      root.querySelector<HTMLElement>('.set-empty button');
    target?.focus();
  }

  private buildRows() {
    this.rows = (this.testContacts || []).map((contact, index) => ({
      contact,
      key: contact.id || contact.uuid || `row-${index}`,
      name: `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || 'No name',
      email: contact.emailAddress || '',
      phone: displayPhone(contact.primaryContactNumber),
      added: contact.createdAt ? formatWhen(contact.createdAt) : '—',
    }));
  }

  get updatedParams() {
    return { ...this.testContactsParam, page: this.testContactsPage };
  }

  updateTestContactsPage(newPage: number) {
    this.testContactsPage = newPage;
    this.pageChange.emit(newPage);
  }

  goToNextPage() {
    this.updateTestContactsPage(this.testContactsPage + 1);
  }

  goToPreviousPage() {
    this.updateTestContactsPage(Math.max(0, this.testContactsPage - 1));
  }

  onPageSizeChange(size: number) {
    this.pageSizeChange.emit(size);
  }

  get testContactsLoading() {
    return this.loading;
  }

  openShowEditModal(testContact: ITestContact) {
    this.selectedTestContact = testContact;
    this.showEditModal = true;
  }

  closeEditModal() {
    this.showEditModal = false;
  }

  openSendModal(testContact: ITestContact) {
    if (!testContact.primaryContactNumber) return;
    this.selectedTestContact = testContact;
    this.showSendModal = true;
  }

  closeSendModal() {
    this.showSendModal = false;
  }

  handleOpenDeletePrompt(testContact: ITestContact) {
    this.selectedTestContact = testContact;
    this.showDeletePrompt = true;
  }

  closeDeletePrompt() {
    this.showDeletePrompt = false;
  }

  deleteContact() {
    const name = this.selectedName;
    const removedId = this.selectedTestContact?.id;
    this.isDeleting = true;
    this.settingService.deleteTestContact(this.selectedTestContact?.id as string).subscribe({
      next: () => {
        this.isDeleting = false;
        const index = this.rows.findIndex((row) => row.contact.id === removedId);
        this.refocusIndex = index < 0 ? 0 : index;
        this.closeDeletePrompt();
        this.fetchTestContacts.emit();
        this.displayFeedback(name ? `${name} removed from test numbers.` : 'Test number removed.', 'success');
      },
      error: (error) => {
        this.isDeleting = false;
        this.closeDeletePrompt();
        const errorMessage =
          error?.error?.message ||
          error?.error?.detail ||
          error?.message ||
          'The test number was not removed. Try again.';
        this.displayFeedback(errorMessage, 'error');
      },
    });
  }

  onUpdateSuccess() {
    this.displayFeedback('Test number saved.', 'success');
  }

  displayFeedback(message: string, type: FeedbackType) {
    this.feedbackMessage = message;
    this.feedbackType = type;
    this.showFeedback = true;
    if (type === 'error' || type === 'warning') {
      this.toast.error(message);
    } else {
      this.toast.success(message);
    }
  }

  ngOnDestroy() {
    // Nothing to clean up: feedback timers live in the toast service.
  }
}
